import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Star, ChatCircleText, Car, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";

const fmtInt = (n) => (n || 0).toLocaleString("pt-BR");
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

function Stars({ value, size = 14 }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={`stars-${value}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          weight={value >= n ? "fill" : "regular"}
          className={value >= n ? "text-amber-400" : "text-slate-200"}
        />
      ))}
    </div>
  );
}

function Kpi({ label, value, hint, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</div>
      <div className={`font-semibold text-[28px] leading-none tracking-tight mt-3 tabular-nums ${accent || "text-slate-900"}`}>
        {value}
      </div>
      {hint && <div className="text-[11.5px] text-slate-400 mt-2">{hint}</div>}
    </div>
  );
}

function Distribution({ items, total }) {
  return (
    <div className="space-y-2.5" data-testid="reviews-distribution">
      {items.map((item) => {
        const pct = total > 0 ? (item.count / total) * 100 : 0;
        return (
          <div key={item.stars} className="flex items-center gap-3" data-testid={`reviews-dist-${item.stars}`}>
            <div className="flex items-center gap-1 w-10 shrink-0">
              <span className="text-[12px] font-medium text-slate-600 tabular-nums">{item.stars}</span>
              <Star size={11} weight="fill" className="text-amber-400" />
            </div>
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-12 text-right text-[12px] text-slate-500 tabular-nums">
              {fmtInt(item.count)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SegBtn({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
        active
          ? "bg-slate-900 text-white"
          : "text-slate-500 hover:text-slate-900 hover:bg-white"
      }`}
    >{label}</button>
  );
}

export default function Reviews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");

  const [days, setDays] = useState(focusId ? 180 : 30);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(focusId ? "negative" : "all");
  const itemRefs = useRef({});
  const [highlightedId, setHighlightedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ days: String(days), limit: "100" });
    if (filter === "positive") params.set("min_rating", "4");
    if (filter === "negative") params.set("max_rating", "2");
    Promise.all([
      api.get(`/reviews/summary?days=${days}`),
      api.get(`/reviews?${params.toString()}`),
    ])
      .then(([s, l]) => {
        if (cancelled) return;
        setSummary(s.data);
        setItems(l.data.items || []);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [days, filter]);

  // Deep-link from notification: scroll to + highlight the focused item.
  useEffect(() => {
    if (!focusId || loading) return;
    const el = itemRefs.current[focusId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(focusId);
      // Decay the highlight after 4s so the page settles back to normal.
      const t = setTimeout(() => setHighlightedId(null), 4000);
      // Drop the focus param so a refresh doesn't re-trigger.
      const t2 = setTimeout(() => {
        const sp = new URLSearchParams(searchParams);
        sp.delete("focus");
        setSearchParams(sp, { replace: true });
      }, 4500);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    // If the focused id isn't in the current list, gracefully expand window
    // (best effort: if it's older than 180d the item still won't render).
  }, [focusId, items, loading, searchParams, setSearchParams]);

  const totalRated = summary?.rated || 0;

  return (
    <>
      <Topbar
        title="Avaliações"
        subtitle="Avaliações capturadas pela IA logo após o atendimento, via WhatsApp. Toda nota e comentário aqui é real e do seu cliente."
        actions={
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1">
            {[7, 30, 90, 180].map((d) => (
              <SegBtn key={d} active={days === d} onClick={() => setDays(d)} label={`${d}d`} testId={`reviews-range-${d}`} />
            ))}
          </div>
        }
      />

      <div className="p-6 max-w-7xl mx-auto" data-testid="reviews-page">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-6">
          <Sparkle size={11} weight="regular" /> Relatórios · Avaliações
        </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Kpi
            label="Avaliações recebidas"
            value={fmtInt(summary.rated)}
            hint={`${fmtInt(summary.sent)} enviadas · taxa ${fmtPct(summary.response_rate_pct)}`}
            testId="reviews-kpi-rated"
          />
          <Kpi
            label="Nota média"
            value={summary.avg_rating ? summary.avg_rating.toFixed(1) : "—"}
            hint={summary.rated ? "Em escala de 1 a 5" : "Sem avaliações no período"}
            accent="text-amber-500"
            testId="reviews-kpi-avg"
          />
          <Kpi
            label="Positivas (4-5⭐)"
            value={fmtPct(summary.positive_pct)}
            hint="Clientes satisfeitos"
            accent="text-emerald-600"
            testId="reviews-kpi-positive"
          />
          <Kpi
            label="Negativas (1-2⭐)"
            value={fmtPct(summary.negative_pct)}
            hint={summary.negative_pct > 0 ? "Vale revisar — clique abaixo" : "Sem clientes insatisfeitos"}
            accent={summary.negative_pct > 10 ? "text-rose-600" : "text-slate-900"}
            testId="reviews-kpi-negative"
          />
        </div>
      )}

      {/* Distribution */}
      {summary && summary.rated > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium mb-4">
            Distribuição
          </div>
          <Distribution items={summary.distribution} total={totalRated} />
        </div>
      )}

      {/* Filter pills */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <SegBtn active={filter === "all"}      onClick={() => setFilter("all")}      label="Todas"           testId="reviews-filter-all" />
        <SegBtn active={filter === "positive"} onClick={() => setFilter("positive")} label="Positivas (4-5⭐)" testId="reviews-filter-positive" />
        <SegBtn active={filter === "negative"} onClick={() => setFilter("negative")} label="Negativas (1-2⭐)" testId="reviews-filter-negative" />
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="reviews-list">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">
            {items.length === 1 ? "1 avaliação" : `${items.length} avaliações`}
          </div>
        </div>

        {loading && (
          <div className="px-5 py-10 text-center text-[13px] text-slate-400" data-testid="reviews-loading">Carregando…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="px-5 py-12 text-center" data-testid="reviews-empty">
            <ChatCircleText size={32} weight="regular" className="mx-auto text-slate-300" />
            <p className="text-[13.5px] text-slate-500 mt-3">
              Nenhuma avaliação no período. Ative <strong className="text-slate-700">Pedir avaliação</strong> no Pós-atendimento.
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {items.map((r, i) => {
              const isFocused = highlightedId && r.id === highlightedId;
              return (
              <li
                key={r.id || i}
                ref={(el) => { if (r.id) itemRefs.current[r.id] = el; }}
                className={`px-5 py-4 transition-all duration-500 ${
                  isFocused
                    ? "bg-amber-50 ring-2 ring-amber-300 ring-inset"
                    : "hover:bg-slate-50"
                }`}
                data-testid={`reviews-item-${i}`}
                data-review-id={r.id}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-[14px] font-semibold text-slate-600">
                    {(r.customer_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[14px] font-semibold text-slate-900 truncate">
                          {r.customer_name || "Cliente"}
                        </div>
                        {r.vehicle_label && (
                          <div className="text-[11.5px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                            <Car size={11} weight="regular" /> {r.vehicle_label}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Stars value={r.rating} size={15} />
                        <span className="text-[10.5px] text-slate-400 tabular-nums">{fmtDate(r.rated_at)}</span>
                      </div>
                    </div>
                    {r.comment && (
                      <p className="text-[13px] text-slate-700 mt-2.5 leading-relaxed whitespace-pre-wrap break-words" data-testid={`reviews-comment-${i}`}>
                        {r.comment}
                      </p>
                    )}
                    {isFocused && r.rating <= 2 && (
                      <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-medium" data-testid="reviews-focus-alert">
                        <Star size={10} weight="fill" /> Avaliação negativa — entre em contato com o cliente
                      </div>
                    )}
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>
      </div>
    </>
  );
}
